import { FC, useRef, useEffect, Fragment, useState } from 'react';
import styles from './index.module.less';
import cn from 'classnames';
import { addEvent, removeEvent } from 'ava-util/dist/dom-event';
import { addClass, removeClass } from 'ava-util/dist/dom-class';
import { useRouter } from 'next/router';
import {
    IParallaxStatus,
    DEFAULT_FRAME_DISTANCE,
    IStatusCanvasImage
} from './interface';
import {
    getCoverStyles,
    getCurIndex,
    getStatusMediaList,
    getStatusContentList,
    loadImages,
    getParallaxTop,
    consoleProps,
    getMergedProps,
    mergeDebugProps,
    getPositionXPercent,
    getFramesLen,
    validFeature,
} from './utils';
import { sum } from 'base/utils/arr';
import { PureVideo } from '@base/lib/PureVideo';
import { IFeatureParallax } from 'mod/featureV2/interface/parallax';
import { getModuleElementId } from 'mod/featureV2/utils';
import { isDark } from 'base/utils/color';

const FADE_DISTANCE = process.env.NEXT_PUBLIC_TERMINAL === 'm' ? 200 : 500;
const TRANSLATE_DISTANCE = 200;
const DEBUG_QUERY = 'debug_parallax';

export const Parallax: FC<IFeatureParallax> = feature => {
    const [props, setProps] = useState<IFeatureParallax>(feature);
    const [canLoad, setCanLoad] = useState<boolean>(false);
    const parallaxRef = useRef<HTMLDivElement>(null);
    const status = useRef<IParallaxStatus | null>(null);
    const { query } = useRouter();

    const {
        moduleId,
        config: {
            cover,
            medias = [],
            framesBeforeStart = 0,
            framesAfterEnd = 0,
            fadeIn = false,
            fadeOut = false,
        },
        list: contentList = [],
    } = props;


    const id = getModuleElementId(moduleId);
    const debugActive = query[DEBUG_QUERY] === id;
    const bgColor = props.background?.color || '#fff';

    const coverStyles = getCoverStyles(cover, medias);

    const getStatus = () => status.current as IParallaxStatus;

    const debugFrame = (unextendedIndex?: number) => {
        if (debugActive) {
            const { debugActiveIndex, debugUnextendedIndex, activeIndex } = getStatus();
            debugActiveIndex.innerText = `${activeIndex}`;
            if (typeof unextendedIndex !== 'undefined') {
                debugUnextendedIndex.innerText = `${unextendedIndex}`;
                removeClass(debugUnextendedIndex, 'hide', false);
            } else {
                addClass(debugUnextendedIndex, 'hide', false);
            }
        }
    };

    const checkInViewport = (): boolean => {
        const status = getStatus();
        const { parallax, screenH } = status;
        const rect = parallax.getBoundingClientRect();
        return rect.top < screenH && rect.bottom > 0;
    };

    const setSize = () => {
        const status = getStatus();
        const { parallax, medias, contentList = [], hideStart, hideEnd } = status;
        const mediaTotalHeight = sum(medias.map(item => item.height));
        parallax.style.height = `${mediaTotalHeight}px`;
        parallax.style.paddingTop = `${hideStart}px`;
        parallax.style.paddingBottom = `${hideEnd}px`;
        const sticky = parallax.querySelector('.g-sticky') as HTMLDivElement;
        const w = sticky.offsetWidth;
        const h = sticky.offsetHeight;
        status.screenW = w;
        status.screenH = h;
        status.activeTop = getParallaxTop(parallax);
        medias.forEach(item => {
            item.elem.width = w;
            item.elem.height = h;
        });

        contentList.forEach(content => {
            if (content.fixed === false) {
                content.elem.style.top = `${content.startTop}px`
            }
        });

        status.parallaxH = parallax.offsetHeight;
        status.inViewport = checkInViewport();
        renderFrame();
    };

    const handleResize = () => {
        setSize();
        handleScroll();
    };

    const renderImage = (canvas: HTMLCanvasElement, statusImage: IStatusCanvasImage, positionX?: string) => {
        const ctx = canvas.getContext('2d');
        const image = statusImage?.image;
        const x = Number(getPositionXPercent(positionX).replace('%', '')) / 100;
        // const positionX = canvas.imagePositionX || '50%';
        if (image && ctx) {
            let dw = image.width;
            let dh = image.height;
            const cw = canvas.width;
            const ch = canvas.height;

            // contain
            /*if (image.width > cw || image.height > ch) {
                if (image.width / image.height >= cw / ch) {
                    dh = (image.height <= ch) ? image.height : ch;
                    dw = Math.floor((dh * image.width) / image.height);
                } else {
                    dw = (image.width <= cw) ? image.width : cw;
                    dh = Math.floor((dw * image.height) / image.width);
                }
            }*/

            //cover
            if (image.width > cw || image.height > ch) {
                if (image.width / image.height >= cw / ch) {
                    dw = Math.round(dw * ch / dh);
                    dh = ch;
                } else {
                    dh = Math.round(cw * dh / dw);
                    dw = cw;
                }
            }

            ctx.clearRect(0, 0, cw, ch);
            ctx.drawImage(image, Math.round((cw - dw) * x), (ch - dh) / 2, dw, dh);
        }
    };

    const getActiveMedia = (): IParallaxStatus['activeMedia'] => {
        const status = getStatus();
        const { medias, activeIndex } = status;
        let activeMedia: IParallaxStatus['activeMedia'] = null;
        medias.forEach(media => {
            if (media.start <= activeIndex && media.end >= activeIndex) {
                activeMedia = media;
                media.elem.style.display = 'block';
            } else {
                media.elem.style.display = 'none';
            }
        });
        return activeMedia;
    };

    const renderContent = (top: number) => {
        top = Math.max(0, top);
        const status = getStatus();
        const { contentList, screenH } = status;
        if (contentList.length === 0) {
            return;
        }
        contentList.forEach(content => {
            const {
                startTop,
                fixed = true,
                animations = ['slide', 'slide'],
                elem,
            } = content;

            let { endTop } = content;
            if (!fixed) {
                endTop = startTop + elem.offsetHeight - screenH - FADE_DISTANCE;
            }

            const startAnime = animations[0];
            const endAnime = animations[1];
            const hasStartAnime = startAnime !== false;
            const hasEndAnime = endAnime !== false;
            if (!hasStartAnime && !hasEndAnime) {
                return false;
            }

            const beforeStart = top < startTop;
            const afterEnd = endTop > 0 && (hasEndAnime ? top > (endTop + FADE_DISTANCE) : top > endTop);
            let visible = !beforeStart && !afterEnd;

            if (!visible) {
                elem.style.visibility = 'hidden';
                return;
            }

            elem.style.visibility = 'visible';
            let opacity = 1;
            let y = 0;

            if (hasStartAnime && top < (startTop + FADE_DISTANCE)) {
                opacity = (top - startTop) / FADE_DISTANCE;
                if (startAnime === 'slide') {
                    y = (1 - opacity) * TRANSLATE_DISTANCE;
                }
            } else if (hasEndAnime && top > endTop) {
                const progress = (top - endTop) / FADE_DISTANCE;
                opacity = 1 - progress;
                if (endAnime === 'slide') {
                    y = (0 - progress) * TRANSLATE_DISTANCE;
                }

            }
            elem.style.opacity = `${opacity}`;
            elem.style.transform = `translateY(${y}px)`;
        });

    };

    const renderFrame = () => {
        const status = getStatus();
        if (!status.inViewport) {
            return;
        }
        let activeMedia = getActiveMedia();
        status.activeMedia = activeMedia;
        let unextendedIndex: number | undefined;
        if (activeMedia && activeMedia.loaded) {
            removeCover();

            // 停止播放视频
            status.medias.forEach(media => {
                if (media.type === 'video' && media.mediaIndex !== activeMedia.mediaIndex) {
                    media.elem.querySelector('video')?.pause();
                }
            })

            if (activeMedia.type === 'canvas') {
                const { activeIndex } = status;
                const statusImageIndex = activeIndex - activeMedia.start;
                const statusImage = activeMedia.images[statusImageIndex];
                if (statusImage?.image) {
                    renderImage(activeMedia.elem, statusImage, activeMedia.imagePositionX);
                    unextendedIndex = statusImage.unextendedIndex;
                }
            } else {
                const video = activeMedia.elem.querySelector('video') as HTMLVideoElement;
                const canPlay = !video.ended || activeMedia.loop;
                if (canPlay) {
                    const rect = status.parallax.getBoundingClientRect();
                    const playing = !video.paused && !video.ended;
                    if (rect.top <= (status.screenH / 2) && rect.bottom >= (status.screenH / 2)) {
                        if (!playing) {
                            video.play();
                        }
                    } else if (playing) {
                        video.pause();
                    }
                }
            }
        }

        debugFrame(unextendedIndex);
    };

    const stepRenderFrames = (n: number) => {
        const status = getStatus();

        if (n === 0) {
            if (status.activeIndex === 0) {
                renderFrame();
            }
            return;
        }
        if (n > 0) {
            n--;
            if (status.activeIndex < status.framesLen - 1) {
                status.activeIndex++;
            }
        } else {
            n++;
            if (status.activeIndex > 0) {
                status.activeIndex--;
            }
        }
        renderFrame();
        stepRenderFrames(n)
    };

    const setParallaxOpacity = (top: number) => {
        const { parallax, screenH, parallaxH, hideStart, hideEnd } = getStatus();
        const endPoint = parallaxH - hideEnd - screenH;

        let opcity: number;
        if (top < hideStart) {
            opcity = fadeIn ? 0 : 1;
        } else if (top >= hideStart && top < (hideStart + FADE_DISTANCE)) {
            // fadeIn
            opcity = fadeIn ? (top - hideStart) / FADE_DISTANCE : 1;
        } else if (top >= (hideStart + FADE_DISTANCE) && top <= (endPoint - FADE_DISTANCE)) {
            opcity = 1;
        } else if (top > (endPoint - FADE_DISTANCE) && top <= endPoint) {
            // fadeOut
            opcity = fadeOut ? (endPoint - top) / FADE_DISTANCE : 1;
        } else {
            opcity = fadeOut ? 0 : 1;
        }
        parallax.style.opacity = `${opcity}`;
    };

    const handleScroll = () => {
        const status = getStatus();
        status.inViewport = checkInViewport();

        // 提前两屏开始懒加载
        // 第一个 media 完整展示后模块透明度由0变为1，开始播放media中的视频或逐帧画面
        // 最后一个 media 底部与屏幕底部重合前，模块透明度由1变为0
        if (!status.loaded) {
            loadMedias(getParallaxTop(status.parallax));
        }

        if (status.inViewport) {
            status.running = true;
            if (status.scrollLock) {
                return;
            }
            status.scrollLock = true;
            requestAnimationFrame(() => {
                const top = getParallaxTop(status.parallax);
                setParallaxOpacity(top);
                renderContent(top);

                const curIndex = getCurIndex(props, status);
                stepRenderFrames(curIndex - status.activeIndex);
                status.activeTop = top;
                status.scrollLock = false;
            });
        } else if (status.running) {
            status.medias.forEach(media => {
                if (media.type === 'video') {
                    media.elem.querySelector('video')?.pause();
                }
            })
            status.running = false;
        }
    };

    const removeCover = () => {
        const status = getStatus();
        if (status.cover) {
            status.cover.remove();
            status.cover = null;
        }
    }

    const loadMedias = (top: number) => {
        const status = getStatus();
        const { screenH, loaded } = status;

        // 懒加载和已加载
        if (loaded || top < (0 - screenH * 2)) {
            return;
        }
        status.loaded = true;
        setCanLoad(true);
    };

    const handleDebugParallax = (event: any) => {
        const props = event.detail;
        if (props.moduleId === feature.moduleId) {
            setProps(validFeature(mergeDebugProps(feature, props)));
            window.localStorage.setItem(id, JSON.stringify(props));
        }
    };

    useEffect(() => {
        if (canLoad) {
            const status = getStatus();
            const { medias, screenW } = status;
            medias.forEach(media => {
                if (media.type === 'canvas') {
                    loadImages({
                        srcList: new Array(media.imageEnd - media.imageFrom + 1)
                            .fill('')
                            .map((_, i) => {
                                const dir = media.imageDir.replace(/\/width-\d+\//, `/width-${screenW * 2}/`);
                                return `${dir}${media.imageFrom + i}.${media.imageFormat || 'jpg'}`;
                            }),
                        onLoad: (i, image) => {
                            media.images.forEach(item => {
                                if (item.unextendedIndex === i) {
                                    item.image = image;
                                }
                            });
                            if (i === 0) {
                                media.loaded = true;
                                renderFrame();
                            }
                        }
                    });
                } else {
                    const video = media.elem.querySelector('video') as HTMLVideoElement;
                    video.addEventListener('loadeddata', () => {
                        if (video.readyState >= 3) {
                            media.loaded = true;
                            video.pause();
                            renderFrame();
                        }
                    });
                    video.load();
                }
            });
        }
    }, [canLoad]);

    useEffect(() => {
        if (debugActive) {
            setProps(validFeature(getMergedProps(feature)))
            document.addEventListener('debugParallax', handleDebugParallax);
            return () => {
                document.removeEventListener('debugParallax', handleDebugParallax);
            }
        }
    }, []);

    useEffect(() => {
        if (debugActive) {
            consoleProps(props);
        }

        const parallax = parallaxRef.current;
        if (!parallax) {
            return
        }
        const medias = getStatusMediaList(props, parallax);

        status.current = {
            parallax,
            cover: parallax.querySelector('.g-child-cover'),
            medias,
            contentList: getStatusContentList(props, parallax, medias),
            framesLen: getFramesLen(props),
            activeTop: 0,
            activeIndex: 0,
            activeMedia: null,
            scrollLock: false,
            screenW: 0,
            screenH: 0,
            parallaxH: 0,
            intervalId: -1,
            hideStart: framesBeforeStart * DEFAULT_FRAME_DISTANCE,
            hideEnd: framesAfterEnd * DEFAULT_FRAME_DISTANCE,
            debugActiveIndex: parallax.querySelector('.g-active-index') as HTMLDivElement,
            debugUnextendedIndex: parallax.querySelector('.g-unextended-index') as HTMLDivElement,
            loaded: false,
            running: true,
            inViewport: false,
        };

        setSize();
        loadMedias(getParallaxTop(parallax));

        addEvent(window, 'resize', handleResize);
        addEvent(window, 'scroll', handleScroll);

        return () => {
            removeEvent(window, 'resize', handleResize)
            removeEvent(window, 'scroll', handleScroll)
        }
    }, [props]);

    return (
        <div
            ref={parallaxRef}
            className={cn(
                styles.parallax,
                { [styles.ahead]: fadeIn }
            )}
        >
            <div className="g-sticky">
                {debugActive && (
                    <div className="g-debug-displayer">
                        <div className="g-active-index"/>
                        <div className="g-unextended-index"/>
                    </div>
                )}

                {coverStyles && (
                    <div
                        className="g-child g-child-cover"
                        style={coverStyles}
                    />
                )}

                {medias.map((media, idx) => {
                    if (media.type === 'canvas') {
                        return (
                            <Fragment key={idx}>
                                <canvas
                                    style={{ display: idx === 0 ? 'block' : 'none' }}
                                    className="g-child g-child-media"
                                />
                            </Fragment>
                        )
                    }

                    const { src, backup, stay, loop = true, ...rest } = media;
                    return (
                        <Fragment key={idx}>
                            <PureVideo
                                style={{
                                    display: idx === 0 ? 'block' : 'none',
                                    background: feature.background?.color
                                }}
                                className="g-child g-child-media"
                                src={canLoad ? [src, backup] : ''}
                                fit={'cover'}
                                loop={loop}
                                forceRender
                                canMultiplePlay
                                muted
                                {...rest}
                            />
                        </Fragment>
                    )
                })}

                {contentList.map((item, idx) => {
                    // item.positonX = 'end'
                    // item.positonY= 'middle'
                    // item.align= 'left'
                    return item.fixed !== false ? (
                        <div
                            key={idx}
                            className={cn('g-child g-child-text', `flex-${item.positonY || 'middle'}`)}
                            style={{
                                visibility: typeof item.start === 'undefined' ? 'visible' : 'hidden',
                                background: item.background ? item.background : void 0,
                            }}
                        >
                            <div className={`g-wrapper flex-1 flex flex-${item.positonX || 'start'}`}>
                                <div className={cn('g-text', `g-text-${isDark(bgColor) ? 'light' : 'dark'}`, `text-${item.align || 'left'}`)}>
                                    <div className="g-title">{item.text.title}</div>
                                    <div className="g-desc">{item.text.description}</div>
                                </div>
                            </div>
                        </div>
                    ) : null
                })};
            </div>

            {contentList.map((item, idx) => item.fixed === false ? (
                <div
                    key={idx}
                    className={'g-scroll-content'}
                    style={{
                        visibility: typeof item.start === 'undefined' ? 'visible' : 'hidden',
                        paddingTop: typeof item.paddingTop !== 'undefined' ? item.paddingTop : '100vh',
                        paddingBottom: typeof item.paddingBottom !== 'undefined' ? item.paddingBottom : '100vh',
                        background: item.background ? item.background : void 0,
                    }}
                >
                    <div className={cn('g-text', `g-text-${isDark(bgColor) ? 'light' : 'dark'}`)}>
                        <div className="g-title">{item.text.title}</div>
                        <div className="g-desc">{item.text.description}</div>
                    </div>
                </div>
            ) : null)}
        </div>
    );
};